import {memo, useCallback, useMemo} from 'react';
import {Controller, useFormContext} from 'react-hook-form';

import {useTranslation} from 'react-i18next';
import DatePicker from 'react-datepicker';

import {LanguageService, useService} from '@innowise-group/core';
import {
  checkTimeIsInInterval,
  checkEventsIsOverlapping,
  getLimitEndTime,
  computeEndTimeWithOffset,
  getStartTime,
  createInnerEvent,
  preventDefaultOnChange,
  computeLimitEndTime,
} from '../../utilities';

import {Form} from '@innowise-group/ui-kit';
import {DatePickerInput} from '../date-picker-input';
import {BookingMeetingRoomFormFields, BookMeetingDatePicker} from '../booking-meeting-room-form';
import {ProcessedMeetingRoomEvent} from '@shared/core';

export type BookMeetingDateProps = {
  eventsData: ProcessedMeetingRoomEvent[];
  name: BookingMeetingRoomFormFields;
};

const timeFields = {
  startTime: 'modals.bookMeetingRoom.start',
  endTime: 'modals.bookMeetingRoom.end',
};

const BookMeetingTime = ({eventsData, name}: BookMeetingDateProps) => {
  const {
    control,
    watch,
    formState: {errors},
    setValue,
  } = useFormContext();
  const {t} = useTranslation();
  const {getAppLanguage} = useService(LanguageService);

  const startDate = watch(BookingMeetingRoomFormFields.StartTime);
  const limitEndTime = useMemo(() => getLimitEndTime(startDate, eventsData), [eventsData, startDate]);

  const onChangeStartTime = useCallback(
    (value: Date | null, onChange: (value: Date | null) => void) => {
      const selectedDate = watch(BookingMeetingRoomFormFields.Date);
      const end = watch(BookingMeetingRoomFormFields.EndTime);

      const updatedValue =
        value && value.getDate() !== selectedDate.getDate() ? getStartTime(value, selectedDate) : value;

      if (updatedValue >= end || (end && checkEventsIsOverlapping(createInnerEvent(updatedValue, end), eventsData))) {
        setValue(BookingMeetingRoomFormFields.EndTime, null, {shouldValidate: true, shouldDirty: true});
      }
      onChange(updatedValue);
    },
    [setValue, watch, eventsData],
  );
  const selectedDate = watch(BookingMeetingRoomFormFields.Date);

  const combineDateAndTime = (time: Date) => {
    if (!selectedDate) return time;
    const combined = new Date(selectedDate);
    combined.setHours(time.getHours(), time.getMinutes(), 0, 0);
    return combined;
  };

  const onCalendarOpenEndTime = useCallback(() => {
    const endTime = watch(BookingMeetingRoomFormFields.EndTime);

    if (startDate && !endTime) {
      const modifiedEndTime = computeEndTimeWithOffset(startDate);
      setValue(BookingMeetingRoomFormFields.EndTime, modifiedEndTime, {shouldValidate: true, shouldDirty: true});
    }
  }, [setValue, startDate, watch]);

  const filterTimeFn = {
    startTime: (date: Date) => !checkTimeIsInInterval(combineDateAndTime(date), eventsData),
    endTime: (date: Date) => {
      const dateTime = combineDateAndTime(date);
      return limitEndTime ? dateTime > startDate && dateTime <= limitEndTime : dateTime > startDate;
    },
  };

  const normalizeDate = (date: Date | null) => {
    if (!date) return null;
    const normalized = new Date(date);
    normalized.setSeconds(0, 0);
    return normalized;
  };

  return (
    <Controller
      name={name}
      control={control}
      defaultValue={null}
      rules={{
        validate: (value) => Boolean(value) || t('modals.messages.required'),
      }}
      render={({field: {onChange, value}}) => {
        return (
          <Form.Field error={errors[name]?.message}>
            <BookMeetingDatePicker type="time">
              <DatePicker
                selected={value}
                locale={getAppLanguage()}
                onChange={(value: Date | null) => {
                  const normalizedValue = normalizeDate(value);

                  name === BookingMeetingRoomFormFields.StartTime
                    ? onChangeStartTime(normalizedValue, onChange)
                    : onChange(normalizedValue);
                }}
                showTimeSelect
                showTimeSelectOnly
                timeIntervals={15}
                timeCaption={t('modals.bookMeetingRoom.time')}
                dateFormat="HH:mm"
                timeFormat="HH:mm"
                placeholderText="__:__"
                popperClassName="popper"
                filterTime={filterTimeFn[name]}
                onChangeRaw={preventDefaultOnChange}
                disabled={!startDate && name === BookingMeetingRoomFormFields.EndTime ? true : false}
                onCalendarOpen={
                  name === BookingMeetingRoomFormFields.EndTime ? () => onCalendarOpenEndTime() : undefined
                }
                customInput={<DatePickerInput value={value} name={name} label={t(timeFields[name])} />}
                injectTimes={name === BookingMeetingRoomFormFields.EndTime ? [computeLimitEndTime(value)] : null}
              />
            </BookMeetingDatePicker>
          </Form.Field>
        );
      }}
    />
  );
};

export default memo(BookMeetingTime);
